/**
 * PageHeader — En-tête de page standardisé pour l'ensemble du backoffice.
 *
 * Motivation (vague 3 lot 1, 2026-09-01) : uniformiser les headers des pages
 * Étudiants qui utilisaient chacun leurs propres classes (text-xl / text-2xl /
 * text-3xl, slate-900 vs gray-900, alignements variables). Ce composant fige
 * la charte : titre 2xl gray-900, description sous forme text-sm gray-500,
 * actions à droite (bouton primaire en dernier).
 *
 * Aucune logique métier — pur wrapper cosmétique.
 */
import React from 'react';

export interface PageHeaderProps {
  /** Titre principal — ex : "Étudiants" */
  title: string;
  /** Sous-titre ou description — ex : "1 234 inscrits" */
  description?: React.ReactNode;
  /** Icône optionnelle affichée à gauche du titre (Lucide) */
  icon?: React.ReactNode;
  /** Actions (boutons) placées à droite du titre */
  actions?: React.ReactNode;
  /** Chip / badge affiché immédiatement à droite du titre (ex: statut) */
  badge?: React.ReactNode;
  /** Bouton retour optionnel (ex: <Button onClick=...>) */
  back?: React.ReactNode;
  /** Style custom si besoin */
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title, description, icon, actions, badge, back, className = '',
}) => {
  return (
    <div className={`flex items-end justify-between gap-3 flex-wrap ${className}`}>
      <div className="flex items-start gap-2 min-w-0">
        {back && <div className="mt-1">{back}</div>}
        {icon && (
          <div className="mt-1 text-gray-400 dark:text-gray-500 flex-shrink-0">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
              {title}
            </h1>
            {badge}
          </div>
          {description && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {description}
            </div>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex flex-wrap gap-1.5 items-center">
          {actions}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
